import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Users, User, GraduationCap, Instagram, Mail, ChevronDown, ChevronUp } from "lucide-react";

const PRODUCTS = [
  {
    id: "masterclass",
    icon: GraduationCap,
    title: "Masterclass",
    subtitle: "Group Learning Experience",
    description: "An intensive group workshop covering advanced Black & Grey Realism techniques. Learn skin theory, needle configurations, shading gradients, and composition from an award-winning artist with 30+ international awards.",
    details: [
      "Multi-day immersive format",
      "Live demonstrations on technique",
      "Skin theory & needle configuration",
      "Shading gradients & tonal range",
      "Composition & reference preparation",
      "Q&A and portfolio review",
    ],
    cta: "Inquire About Next Masterclass",
  },
  {
    id: "seminar",
    icon: User,
    title: "1-on-1 Seminar",
    subtitle: "Personalized Virtual Training",
    description: "A focused, private session tailored to your skill level and goals. Work directly with Lud to break down your specific challenges — whether it's realism rendering, grey wash control, or portfolio development.",
    details: [
      "Private one-on-one format",
      "Customized to your skill level",
      "Technique breakdown & correction",
      "Grey wash mixing & control",
      "Reference selection guidance",
      "Follow-up resources provided",
    ],
    cta: "Book a 1-on-1 Seminar",
  },
  {
    id: "mentorship",
    icon: Users,
    title: "1-on-1 In-Person Mentorship",
    subtitle: "Hands-On Studio Training",
    description: "The most comprehensive learning experience — train alongside Lud at Avalon Tattoo Collective in New Westminster, BC. Get hands-on guidance, real-time feedback on live work, and direct mentorship from one of Canada's top realism artists.",
    details: [
      "In-person at Avalon Tattoo Collective",
      "Hands-on with real clients (supervised)",
      "Station setup & workflow optimization",
      "Business & client management insights",
      "Extended mentorship period",
      "Ongoing support after program",
    ],
    cta: "Apply for Mentorship",
  },
];

function ProductCard({ product, index }) {
  const [expanded, setExpanded] = useState(false);
  const Icon = product.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 + index * 0.15, duration: 0.6 }}
      className="bg-ink-900 border border-ink-600 rounded-sm overflow-hidden group hover:border-verde/40 transition-colors duration-500"
    >
      <div className="p-6 md:p-10">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 border border-verde/30 rounded-sm flex items-center justify-center bg-verde/5">
            <Icon size={20} className="text-verde" />
          </div>
          <div>
            <h3 className="font-cinzel text-xl md:text-2xl font-bold text-[#E5E5E5]">{product.title}</h3>
            <p className="font-manrope text-[10px] tracking-widest uppercase text-verde">{product.subtitle}</p>
          </div>
        </div>

        <p className="font-manrope text-sm text-[#A3A3A3] leading-relaxed mb-6">
          {product.description}
        </p>

        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-2 font-manrope text-[10px] tracking-widest uppercase text-[#525252] hover:text-verde transition-colors mb-4"
        >
          {expanded ? "Hide" : "What's included"}
          {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </button>

        {expanded && (
          <motion.ul
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="space-y-2 mb-6 border-l-2 border-verde/20 pl-4"
          >
            {product.details.map((d, i) => (
              <li key={i} className="font-manrope text-xs text-[#A3A3A3] flex items-start gap-2">
                <span className="w-1 h-1 bg-verde rounded-full mt-1.5 flex-shrink-0" />
                {d}
              </li>
            ))}
          </motion.ul>
        )}

        <a
          href={`mailto:ludbradley.tattoo@gmail.com?subject=${encodeURIComponent(product.title + " Inquiry")}&body=${encodeURIComponent("Hi Lud,\n\nI'm interested in the " + product.title + ". Could you share more details?\n\nThanks!")}`}
          data-testid={`cta-${product.id}`}
          className="inline-flex items-center gap-3 btn-verde w-full justify-center py-3"
        >
          {product.cta}
          <ArrowRight size={14} />
        </a>
      </div>
    </motion.div>
  );
}

export default function ClassLanding() {
  return (
    <div className="min-h-screen bg-ink-950">
      {/* Header */}
      <header className="border-b border-ink-600 px-6 md:px-12 py-5">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <a href="https://ludbradleytattoo.com" className="font-cinzel text-sm tracking-[0.3em] uppercase text-[#E5E5E5] hover:text-verde transition-colors">
            Lud Bradley
          </a>
          <a
            href="https://www.instagram.com/ludbradleytattoo/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 font-manrope text-[10px] tracking-widest uppercase text-[#525252] hover:text-verde transition-colors"
          >
            <Instagram size={14} />
            @ludbradleytattoo
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 md:py-32 px-6 md:px-12">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="font-manrope text-verde text-xs tracking-[0.3em] uppercase mb-6">Education by Lud Bradley</p>
            <h1 className="font-cinzel text-4xl sm:text-5xl lg:text-6xl font-bold text-[#E5E5E5] leading-tight mb-6">
              Master Black &amp; Grey<br />Realism
            </h1>
            <p className="font-manrope text-[#525252] text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              Learn directly from an internationally recognized artist with 30+ awards.
              Three ways to level up your craft — from group workshops to hands-on studio mentorship.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products */}
      <section className="pb-24 md:pb-32 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
            {PRODUCTS.map((product, i) => (
              <ProductCard key={product.id} product={product} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-ink-600">
        <div className="max-w-2xl mx-auto text-center">
          <p className="font-manrope text-[10px] tracking-widest uppercase text-verde mb-4">Questions?</p>
          <h2 className="font-cinzel text-2xl md:text-3xl font-bold text-[#E5E5E5] mb-4">
            Not sure which is right for you?
          </h2>
          <p className="font-manrope text-sm text-[#525252] mb-8 leading-relaxed">
            Reach out and Lud will help you choose the best path based on your experience level and goals.
          </p>
          <a
            href="mailto:ludbradley.tattoo@gmail.com?subject=Education%20Inquiry"
            data-testid="class-contact-cta"
            className="inline-flex items-center gap-3 btn-verde"
          >
            <Mail size={14} />
            ludbradley.tattoo@gmail.com
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-ink-600 py-8 px-6 md:px-12">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-manrope text-[10px] tracking-widest uppercase text-[#404040]">
            Lud Bradley Education &middot; Avalon Tattoo Collective &middot; New Westminster, BC
          </p>
          <div className="flex items-center gap-6">
            <a href="https://ludbradleytattoo.com" className="font-manrope text-[10px] tracking-widest uppercase text-[#525252] hover:text-verde transition-colors">
              Portfolio
            </a>
            <a href="https://avalontattoocollective.ca" target="_blank" rel="noopener noreferrer" className="font-manrope text-[10px] tracking-widest uppercase text-[#525252] hover:text-verde transition-colors">
              Avalon Collective
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
